import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import ModalFooter from '../common/modal/footer';
import ModalHeader from '../common/modal/header';
import PropTypes from 'prop-types';
import CustomHtmlSlot from '@/common/components/CustomHtmlSlot';

const { Order } = Mobi2Go;

const ShowError = ({ error }) => {
    const [t] = useTranslation('order.curbside-pickup');

    return (
        <span id={`m2g-curbside-pickup-error-${error}`}>
            {t(`error-message-${error}`)}
        </span>
    );
};

const renderError = (errors, field) => {
    return errors && errors.includes(field) && <ShowError error={field} />;
};

function CurbsidePickupForm({ closeModal }) {
    const [t] = useTranslation('order.curbside-pickup');
    const [details, setDetails] = useState(Order.getCurbsidePickupDetails());
    const [errors, setErrors] = useState([]);
    const is_curbside_pickup = Order.getLocation()?.isCurbsidePickupEnabled();

    useEffect(() => {
        Order.setCurbsidePickupDetails(details);
    }, [details])

    const handleSubmit = e => {
        let submitErrors = [];
        Order.setCurbsidePickupDetails(details);
        Object.keys(details).forEach(key => {
            if (details[key] === '' && key !== 'plate_number') {
                submitErrors.push(key);
            }
        });

        if (submitErrors && submitErrors.length === 0) {
            Order.log('Curbside pickup details were set', details);
            closeModal();
        }
        setErrors(submitErrors);
    };

    return (
        <>
            <ModalHeader onClose={closeModal} headerText={t('title')} />
            <div className="m2g-modal-body m2g-order-curbside-modal-body">
                <div className="m2g-curbside-pickup-form">
                    <CustomHtmlSlot slot="curbside-pickup-form-above" />
                    <div
                        className="m2g-curbside-pickup-vehicle-details"
                        data-visible={is_curbside_pickup}
                    >
                        <div className="m2g-curbside-pickup-form-title">
                            {t('car-title')}
                        </div>
                        <div>
                            <div
                                className="m2g-curbside-pickup-details"
                                id="m2g-curbside-pickup-brand"
                            >
                                <div
                                    className="m2g-curbside-pickup-details-label"
                                    id="m2g-curbside-pickup-brand-label"
                                >
                                    {t(`brand-label`)}
                                </div>

                                <input
                                    type="text"
                                    inputMode="text"
                                    placeholder={t('brand-placeholder')}
                                    className="m2g-input m2g-curbside-pickup-input m2g-curbside-pickup-details-input"
                                    id="m2g-curbside-pickup-brand-input"
                                    value={details.brand}
                                    maxLength="20"
                                    onChange={e => {
                                        const { value } = e.target;
                                        setDetails(prevState => ({
                                            ...prevState,
                                            brand: value,
                                        }));
                                    }}
                                />
                            </div>
                            <div className="m2g-curbside-pickup-error">
                                {renderError(errors, 'brand')}
                            </div>
                        </div>
                        <div>
                            <div
                                className="m2g-curbside-pickup-details"
                                id="m2g-curbside-pickup-make"
                            >
                                <div
                                    className="m2g-curbside-pickup-details-label"
                                    id="m2g-curbside-pickup-make-label"
                                >
                                    {t(`make-label`)}
                                </div>

                                <input
                                    type="text"
                                    inputMode="text"
                                    placeholder={t('make-placeholder')}
                                    className="m2g-input m2g-curbside-pickup-input m2g-curbside-pickup-details-input"
                                    id="m2g-curbside-pickup-make-input"
                                    value={details.make}
                                    maxLength="20"
                                    onChange={e => {
                                        const { value } = e.target;
                                        setDetails(prevState => ({
                                            ...prevState,
                                            make: value,
                                        }));
                                    }}
                                />
                            </div>
                            <div className="m2g-curbside-pickup-error">
                                {renderError(errors, 'make')}
                            </div>
                        </div>
                        <div>
                            <div
                                className="m2g-curbside-pickup-details"
                                id="m2g-curbside-pickup-colour"
                            >
                                <div
                                    className="m2g-curbside-pickup-details-label"
                                    id="m2g-curbside-pickup-colour-label"
                                >
                                    {t(`colour-label`)}
                                </div>

                                <input
                                    type="text"
                                    inputMode="text"
                                    placeholder={t('colour-placeholder')}
                                    className="m2g-input m2g-curbside-pickup-input m2g-curbside-pickup-details-input"
                                    id="m2g-curbside-pickup-colour-input"
                                    value={details.colour}
                                    maxLength="20"
                                    onChange={e => {
                                        const { value } = e.target;
                                        setDetails(prevState => ({
                                            ...prevState,
                                            colour: value,
                                        }));
                                    }}
                                />
                            </div>
                            <div className="m2g-curbside-pickup-error">
                                {renderError(errors, 'colour')}
                            </div>
                        </div>
                        <div>
                            <div
                                className="m2g-curbside-pickup-details"
                                id="m2g-curbside-pickup-plate-number"
                            >
                                <div
                                    className="m2g-curbside-pickup-details-label"
                                    id="m2g-curbside-pickup-plate-number-label"
                                >
                                    {t(`license-plate-number-label`)}
                                </div>

                                <input
                                    type="text"
                                    inputMode="text"
                                    placeholder={t(
                                        'license-plate-number-placeholder'
                                    )}
                                    className="m2g-input m2g-curbside-pickup-input m2g-curbside-pickup-details-input"
                                    id="m2g-curbside-pickup-plate-number-input"
                                    value={details.plate_number}
                                    maxLength="20"
                                    onChange={e => {
                                        const { value } = e.target;
                                        setDetails(prevState => ({
                                            ...prevState,
                                            plate_number: value,
                                        }));
                                    }}
                                />
                            </div>
                            <div className="m2g-curbside-pickup-error">
                                {renderError(errors, 'plate_number')}
                            </div>
                        </div>
                    </div>
                    <CustomHtmlSlot slot="curbside-pickup-form-details-below" />
                </div>
                <CustomHtmlSlot slot="curbside-pickup-form-below" />
            </div>
            <ModalFooter>
                <ModalFooter.Right>
                    <button
                        type="button"
                        className="m2g-button m2g-button--primary m2g-button--fat"
                        onClick={handleSubmit}
                    >
                        {t('next-button-label')}
                    </button>
                </ModalFooter.Right>
            </ModalFooter>
        </>
    );
}

CurbsidePickupForm.propTypes = {
    closeModal: PropTypes.func.isRequired,
};

export default CurbsidePickupForm;
